import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, ArrowRight, CheckCircle2, ShieldCheck, Lock, FileSearch, Radar } from "lucide-react";
import { useContent } from "@/lib/useContent";
import { slugify } from "@/lib/slug";
import { assetUrl } from "@/lib/api";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Reveal } from "@/components/landing/Reveal";
import { ICONS } from "@/lib/icons";
import { openLeadForm } from "@/components/LeadFormDialog";

const TRUST = [
  { icon: ShieldCheck, label: "100% Compliant" },
  { icon: Lock, label: "SSL & 2FA Secured" },
  { icon: FileSearch, label: "Regular Security Audits" },
  { icon: Radar, label: "Fraud Monitoring" },
];

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const content = useContent();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  if (!content) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FAF9F5]" data-testid="service-page-loader">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#8896AB]">loading</span>
      </div>
    );
  }

  const services = content.services || [];
  const idx = services.findIndex((s) => slugify(s.title) === slug);
  if (idx === -1) {
    return (
      <div className="min-h-screen bg-[#FAF9F5] text-[#0F1B2B]">
        <Navbar brand={content.brand} />
        <div className="mx-auto max-w-4xl px-5 pt-44 pb-24 text-center" data-testid="service-not-found">
          <h1 className="font-syne text-3xl font-bold">Service not found</h1>
          <Link to="/#services" className="mt-4 inline-block font-mono text-sm text-[#0E8A8A]">← Back to all services</Link>
        </div>
        <Footer brand={content.brand} contact={content.contact} social={content.social} footer={content.footer} />
      </div>
    );
  }

  const s = services[idx];
  const Icon = ICONS[s.icon] || ICONS.Zap;
  const points = s.detailPoints || [];
  const others = services.filter((_, i) => i !== idx).slice(0, 3);
  const prev = services[(idx - 1 + services.length) % services.length];
  const next = services[(idx + 1) % services.length];

  return (
    <div className="min-h-screen bg-[#FAF9F5] text-[#0F1B2B]" data-testid="service-detail-page">
      <Navbar brand={content.brand} />

      <section className="relative overflow-hidden pt-32 pb-16 lg:pt-40 lg:pb-20">
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(15,27,43,0.045)_1px,transparent_1px),linear-gradient(90deg,rgba(15,27,43,0.045)_1px,transparent_1px)] bg-[size:72px_72px] [mask-image:radial-gradient(ellipse_at_top,black_30%,transparent_75%)]" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:gap-20 lg:px-8">
          <div>
            <Reveal>
              <div className="mb-6 flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.2em] text-[#8896AB]">
                <Link to="/#services" className="transition-colors hover:text-[#0E8A8A]" data-testid="breadcrumb-services">Services</Link>
                <span>/</span>
                <span className="text-[#0E8A8A]" data-testid="breadcrumb-current">{s.title}</span>
              </div>
            </Reveal>
            <Reveal delay={0.06}>
              <div className="mb-6 flex items-center gap-4">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0E8A8A]/10 text-[#0E8A8A]">
                  <Icon size={26} strokeWidth={1.8} />
                </span>
                <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#B8860B]">{String(idx + 1).padStart(2, "0")} / {String(services.length).padStart(2, "0")}</span>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <h1 className="font-syne text-4xl font-bold leading-[1.08] tracking-tight sm:text-5xl" data-testid="service-title">{s.title}</h1>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-[#5A6578] sm:text-lg" data-testid="service-brief">
                {s.detailBrief || s.description}
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button onClick={openLeadForm} data-testid="service-cta-primary"
                  className="group inline-flex items-center gap-2 rounded-full bg-[#1B3A5C] px-7 py-3.5 text-sm font-bold text-white transition-all duration-300 hover:bg-[#0E8A8A] hover:shadow-[0_16px_40px_-12px_rgba(14,138,138,0.5)] active:scale-95">
                  Get This Service <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </button>
                <a href={`mailto:${content.contact.email}`} data-testid="service-cta-sales"
                  className="rounded-full border border-[#E2DDD5] bg-white px-7 py-3.5 text-sm font-semibold text-[#0F1B2B] transition-colors duration-300 hover:border-[#0E8A8A]/50 hover:text-[#0E8A8A]">
                  Talk to Sales
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="absolute -right-4 -top-4 h-full w-full rounded-2xl border-2 border-[#B8860B]/30" />
            {(s.bannerUrl || s.imageUrl) ? (
              <img src={assetUrl(s.bannerUrl || s.imageUrl)} alt={s.title} className="relative aspect-[4/3] w-full rounded-2xl object-cover shadow-[0_30px_60px_-30px_rgba(15,27,43,0.35)]" data-testid="service-image" />
            ) : (
              <div className="relative flex aspect-[4/3] w-full items-center justify-center rounded-2xl bg-[#F2EFE9]">
                <Icon size={72} className="text-[#0E8A8A]/30" />
              </div>
            )}
          </Reveal>
        </div>
      </section>

      {points.length > 0 && (
        <section className="border-t border-[#E2DDD5] bg-white py-20" data-testid="service-highlights">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <div className="mb-10 flex items-center gap-3">
                <span className="font-mono text-xs text-[#B8860B]">{"\u2726"}</span>
                <span className="h-px w-10 bg-[#B8860B]/40" />
                <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#0E8A8A]">What You Get</span>
              </div>
            </Reveal>
            <div className="grid gap-x-10 gap-y-6 sm:grid-cols-2">
              {points.map((pt, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="flex items-start gap-4 border-b border-[#EDE8DE] pb-6" data-testid={`service-point-${i}`}>
                    <span className="font-mono text-sm font-semibold text-[#B8860B]">{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex items-start gap-2.5">
                      <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-[#0E8A8A]" />
                      <p className="font-syne text-base font-semibold text-[#1B3A5C] sm:text-lg">{pt}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-[#0B131F] py-16" data-testid="service-trust-band">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-8 lg:flex-row lg:items-center">
            <h2 className="max-w-sm font-syne text-2xl font-bold tracking-tight text-[#F8FAFC]">
              Security you never have to <span className="text-[#E3B23C]">lose sleep over</span>
            </h2>
            <div className="grid grid-cols-2 gap-x-10 gap-y-5 sm:grid-cols-4">
              {TRUST.map((t, i) => (
                <div key={i} className="flex items-center gap-2.5" data-testid={`trust-${i}`}>
                  <t.icon size={16} className="shrink-0 text-[#E3B23C]" />
                  <span className="font-mono text-xs uppercase tracking-[0.12em] text-[#8CA0B8]">{t.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" data-testid="service-related">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="mb-10 flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-syne text-2xl font-bold tracking-tight">Explore more services</h2>
            <div className="flex items-center gap-2">
              <Link to={`/services/${slugify(prev.title)}`} data-testid="service-prev"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E2DDD5] bg-white text-[#1B3A5C] transition-colors hover:border-[#0E8A8A]/50 hover:text-[#0E8A8A]">
                <ArrowLeft size={16} />
              </Link>
              <Link to={`/services/${slugify(next.title)}`} data-testid="service-next"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E2DDD5] bg-white text-[#1B3A5C] transition-colors hover:border-[#0E8A8A]/50 hover:text-[#0E8A8A]">
                <ArrowRight size={16} />
              </Link>
            </div>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {others.map((o, i) => {
              const OIcon = ICONS[o.icon] || ICONS.Zap;
              return (
                <Reveal key={i} delay={i * 0.08}>
                  <Link to={`/services/${slugify(o.title)}`} data-testid={`related-service-${i}`}
                    className="group block overflow-hidden rounded-2xl border border-[#E2DDD5] bg-white shadow-sm transition-all duration-300 hover:border-[#0E8A8A]/40 hover:shadow-md">
                    {o.imageUrl && (
                      <div className="h-36 overflow-hidden">
                        <img src={assetUrl(o.imageUrl)} alt={o.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center justify-between">
                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#0E8A8A]/10 text-[#0E8A8A]">
                          <OIcon size={18} />
                        </span>
                        <span className="inline-flex items-center gap-1 font-mono text-[11px] font-semibold uppercase tracking-[0.15em] text-[#0E8A8A]">
                          Know More <ArrowUpRight size={12} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </span>
                      </div>
                      <h3 className="mt-4 font-syne text-lg font-semibold">{o.title}</h3>
                      <p className="mt-2 line-clamp-2 text-sm text-[#5A6578]">{o.description}</p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <Footer brand={content.brand} contact={content.contact} social={content.social} footer={content.footer} />
    </div>
  );
}
